import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { extractTableDataFromElement, tableDataToCSV, tableDataToMarkdown } from 'streamdown';

import {
	Box,
	CheckOutline,
	DocumentDownloadOutline,
	DuplicateOutline,
	Flex,
	IconButton,
	Table,
	Tbody,
	Td,
	Th,
	Thead,
	Tooltip,
	Tr,
} from '@bonusly/design-system';

const tableActionStyles = {
	size: 'xs' as const,
	variant: 'ghost' as const,
	colorScheme: 'secondary' as const,
	color: 'info.icon',
	borderRadius: '8px',
	_hover: { bg: 'secondary.bgAlpha', color: 'secondary.fgActive' },
	_active: { bg: 'secondary.bgAlphaHover', color: 'secondary.fgActive' },
};

interface BizyMarkdownTableProps {
	children: React.ReactNode;
}

export function BizyMarkdownTable({ children }: BizyMarkdownTableProps) {
	const { t } = useTranslation('admin_bizy');
	const tableRef = useRef<HTMLTableElement>(null);
	const [copied, setCopied] = useState(false);
	const copyTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

	useEffect(() => {
		return () => {
			if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
		};
	}, []);

	const handleCopy = useCallback(async () => {
		const table = tableRef.current;
		if (!table) return;
		try {
			const data = extractTableDataFromElement(table);
			await navigator.clipboard.writeText(tableDataToMarkdown(data));
			setCopied(true);
			if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
			copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
		} catch {
			// Clipboard write failed silently
		}
	}, []);

	const handleDownload = useCallback(() => {
		const table = tableRef.current;
		if (!table) return;
		try {
			const data = extractTableDataFromElement(table);
			const csv = tableDataToCSV(data);
			const blob = new Blob([csv], { type: 'text/csv' });
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = 'table.csv';
			a.click();
			URL.revokeObjectURL(url);
		} catch {
			// Download failed silently
		}
	}, []);

	return (
		<Box mt={4} mb={2} sx={{ '&:hover .bizy-table-actions': { opacity: 1 } }}>
			<Box overflowX="auto" borderRadius="md" border="1px solid" borderColor="border.default" sx={{ 'tr:last-of-type td': { borderBottom: 'none' } }}>
				<Table ref={tableRef} variant="simple" size="sm" color="inherit">
					{children}
				</Table>
			</Box>
			<Flex className="bizy-table-actions" gap="4px" mt={2} opacity={0} transition="opacity 0.15s ease-in-out">
				<Tooltip label={copied ? t('feedback.copied') : t('feedback.copyTable')} placement="top">
					<IconButton
						aria-label={t('feedback.copyTable')}
						icon={copied ? <CheckOutline boxSize={5} /> : <DuplicateOutline boxSize={5} />}
						{...tableActionStyles}
						onClick={handleCopy}
					/>
				</Tooltip>
				<Tooltip label={t('feedback.downloadCsv')} placement="top">
					<IconButton aria-label={t('feedback.downloadCsv')} icon={<DocumentDownloadOutline boxSize={5} />} {...tableActionStyles} onClick={handleDownload} />
				</Tooltip>
			</Flex>
		</Box>
	);
}

export const bizyTableComponents = (fontSize: string) => ({
	table: ({ children }: React.TableHTMLAttributes<HTMLTableElement>) => <BizyMarkdownTable>{children}</BizyMarkdownTable>,
	thead: ({ children }: React.HTMLAttributes<HTMLTableSectionElement>) => <Thead>{children}</Thead>,
	tbody: ({ children }: React.HTMLAttributes<HTMLTableSectionElement>) => <Tbody>{children}</Tbody>,
	tr: ({ children }: React.HTMLAttributes<HTMLTableRowElement>) => <Tr>{children}</Tr>,
	th: ({ children, style }: React.ThHTMLAttributes<HTMLTableCellElement>) => (
		<Th fontSize={fontSize} fontWeight="semibold" color="inherit" px={4} py={2.5} style={style}>
			{children}
		</Th>
	),
	td: ({ children, style }: React.TdHTMLAttributes<HTMLTableCellElement>) => (
		<Td fontSize={fontSize} color="inherit" style={style}>
			{children}
		</Td>
	),
});
